// === Catalogue country switch (superadmin global catalogue) ===
// The global product catalogue is split by country. The countries come from
// public.countries (lib/countries.ts), so a new country appears here once its
// row is added. AU is the default everywhere — an admin who never touches the
// switch sees exactly the AU catalogue, as before.

import { useCallback, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { UkAddressFields, type UkAddressValue } from './UkAddressFields';
import { findCountry, useCountries, type Country } from '../lib/countries';

/** A public.countries code, e.g. 'AU', 'GB'. */
export type CatalogueCountry = string;

const STORAGE_KEY = 'c2g_admin_catalogue_country';

/** Normalise any stored / API value to a country code (anything not a code = AU). */
export function toCatalogueCountry(value: unknown): CatalogueCountry {
  return typeof value === 'string' && /^[A-Z]{2}$/.test(value) ? value : 'AU';
}

/** Currency sign for catalogue cost fields, from the country's currency ('$' until loaded). */
export function catalogueCurrency(countries: readonly Country[], country: CatalogueCountry): string {
  return findCountry(countries, country)?.currency_symbol ?? '$';
}

/** Country name for captions ("United Kingdom"), or the code until loaded. */
export function catalogueCountryName(countries: readonly Country[], country: CatalogueCountry): string {
  return findCountry(countries, country)?.name ?? country;
}

function readStored(): CatalogueCountry {
  try {
    return toCatalogueCountry(window.localStorage.getItem(STORAGE_KEY));
  } catch {
    return 'AU';
  }
}

/** Selected catalogue country (default 'AU'), remembered per browser. */
export function useCatalogueCountry(): readonly [CatalogueCountry, (c: CatalogueCountry) => void] {
  const [country, setCountryState] = useState<CatalogueCountry>(readStored);
  const setCountry = useCallback((c: CatalogueCountry) => {
    setCountryState(c);
    try {
      window.localStorage.setItem(STORAGE_KEY, c);
    } catch {
      // storage unavailable — selection stays in component state only
    }
  }, []);
  return [country, setCountry] as const;
}

interface Props {
  readonly value: CatalogueCountry;
  readonly onChange: (c: CatalogueCountry) => void;
}

export function CatalogueCountrySwitch({ value, onChange }: Props) {
  const countries = useCountries();
  // Active countries, plus the selected one even if it was deactivated.
  const options = countries.filter((c) => c.active || c.code === value);
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
      <ToggleButtonGroup
        size="small"
        exclusive
        color="primary"
        value={value}
        onChange={(_, v: CatalogueCountry | null) => { if (v) onChange(v); }}
        aria-label="Catalogue country"
      >
        {(options.length > 0 ? options : [{ code: value, name: value }]).map((c) => (
          <ToggleButton key={c.code} value={c.code}>{c.name}</ToggleButton>
        ))}
      </ToggleButtonGroup>
      {value !== 'AU' && (
        <Typography variant="caption" color="warning.main" sx={{ fontWeight: 600 }}>
          Showing {catalogueCountryName(countries, value)} catalogue
        </Typography>
      )}
    </Box>
  );
}

// ---------------------------------------------------------------------------
// UK address for catalogue records (suppliers / manufacturers / soil labs).
// Those tables keep the AU shape (suburb = suburb id, state = state id); a GB
// record stores the town TEXT in `suburb`, the county TEXT in `state`, and
// country 'UNITED KINGDOM'. AU records never render this.
// ---------------------------------------------------------------------------

export const UK_COUNTRY_NAME = 'UNITED KINGDOM';

export interface CatalogueUkAddress {
  readonly suburb: string;
  readonly state: string;
  readonly postcode: string;
}

interface UkAddressProps {
  readonly apiUrl: string;
  readonly token: string;
  readonly value: CatalogueUkAddress;
  readonly onChange: (patch: Partial<CatalogueUkAddress> & { readonly country: string }) => void;
}

export function CatalogueUkAddressFields({ apiUrl, token, value, onChange }: UkAddressProps) {
  const ukValue: UkAddressValue = {
    postcode: value.postcode,
    town: value.suburb,
    county: value.state,
    lat: '',
    lon: '',
  };
  return (
    <Box sx={{ width: '100%' }}>
      <UkAddressFields
        apiUrl={apiUrl}
        token={token}
        value={ukValue}
        onChange={(p) => onChange({
          ...(p.postcode !== undefined ? { postcode: p.postcode } : {}),
          ...(p.town !== undefined ? { suburb: p.town } : {}),
          ...(p.county !== undefined ? { state: p.county } : {}),
          country: UK_COUNTRY_NAME,
        })}
      />
    </Box>
  );
}
